import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button, cn } from '@hrms/ui';
import { BrandMark } from '../../components/BrandMark.js';
import { claimOnBrandSystemBars } from '../../lib/nativeSplash.js';
import { IntroIllustration } from './IntroIllustrations.js';
import { hasSeenIntro, introEligible, markIntroSeen } from './introSeen.js';
import { INTRO_SLIDES } from './introSlides.js';

const LAST = INTRO_SLIDES.length - 1;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Faint dashed orbits across each slide's hero — the brand mark's idea at illustration size.
 *  Decoration only; it never moves, so there is nothing for reduced motion to still. */
function IntroOrbits() {
  return (
    <svg viewBox="-170 -170 340 340" aria-hidden="true" className="intro-orbits">
      <circle r="165" fill="none" stroke="currentColor" strokeOpacity="0.18" strokeDasharray="2 7" />
      <circle r="118" fill="none" stroke="currentColor" strokeOpacity="0.22" strokeDasharray="3 6" />
      <circle r="72" fill="none" stroke="currentColor" strokeOpacity="0.14" strokeDasharray="2 6" />
      <circle cx="-150" cy="-30" r="3.5" fill="currentColor" fillOpacity="0.5" />
      <circle cx="-108" cy="48" r="4.5" fill="currentColor" fillOpacity="0.4" />
      <circle cx="112" cy="-38" r="3" fill="currentColor" fillOpacity="0.45" />
    </svg>
  );
}

/**
 * The phone app's first-run intro: three benefit slides between the launch splash and sign-up.
 * Only a first-time visitor on the app who would otherwise land on sign-up ever sees it, and only
 * once — any way out (Skip, Get started, Sign in) marks it seen, and every exit replaces this entry
 * so Android's back button cannot walk someone back into it.
 *
 * The slides are a native CSS scroll-snap track rather than a carousel library: the WebView's own
 * swipe physics feel right on a phone. Buttons (Next, the dots, arrow keys) set the slide at once
 * and scroll the track to it; the scroll listener follows swipes, and ignores the in-between
 * positions while a button-driven scroll is still travelling.
 *
 * The top of every slide is a coloured hero in that slide's own gradient (the --intro-* tokens,
 * picked by `data-tone` in styles.css) with the mark in the corner and an illustration of floating
 * cards, so the blue splash fades into something continuous rather than a white form. The screen's
 * tone follows the current slide, which recolours the button and dots with it.
 */
export function IntroPage() {
  // Decided once: marking the intro seen on the way out must not flip this into a redirect mid-exit.
  const [eligible] = useState(() => introEligible() && !hasSeenIntro());
  const [index, setIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // The slide a button-driven smooth scroll is heading for. While it is set, the scroll listener
  // ignores the in-between positions — otherwise the first frames (still nearer the old slide)
  // round back to the old index and the footer flickers Next / Get started on the way.
  const targetRef = useRef<number | null>(null);

  const goTo = useCallback((next: number) => {
    const clamped = Math.max(0, Math.min(LAST, next));
    setIndex(clamped);
    const track = trackRef.current;
    // jsdom (and very old WebViews) have no element scrollTo; the index above still moves.
    if (!track || typeof track.scrollTo !== 'function') return;
    const smooth = !prefersReducedMotion();
    targetRef.current = smooth ? clamped : null;
    track.scrollTo({ left: clamped * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // A swipe moves the track, not React state — read the slide back off the scroll position.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const width = track.clientWidth;
      if (width <= 0) return;
      const i = Math.max(0, Math.min(LAST, Math.round(track.scrollLeft / width)));
      if (targetRef.current !== null) {
        if (i === targetRef.current) targetRef.current = null;
        return;
      }
      setIndex(i);
    };
    // If the person grabs the track mid-animation the target is never reached; scrollend lets the
    // swipe take over again. WebViews without it fall back to clearing on reaching the target.
    const onScrollEnd = () => {
      targetRef.current = null;
      onScroll();
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    const hasScrollEnd = 'onscrollend' in window;
    if (hasScrollEnd) track.addEventListener('scrollend', onScrollEnd);
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (hasScrollEnd) track.removeEventListener('scrollend', onScrollEnd);
    };
  }, [eligible]);

  // The hero runs under the status bar: light status-bar icons while the intro is up. The footer
  // under the navigation bar is white, so that bar keeps the theme's dark icons.
  useEffect(() => (eligible ? claimOnBrandSystemBars('status') : undefined), [eligible]);

  if (!eligible) return <Navigate to="/signup" replace />;

  const leave = (to: '/signup' | '/login') => {
    markIntroSeen();
    navigate(to, { replace: true });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(index + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(index - 1);
    }
  };

  const onLast = index === LAST;

  return (
    <div
      data-tone={INTRO_SLIDES[index]!.id}
      className="intro-screen relative flex h-dvh flex-col overflow-hidden bg-bg text-fg"
    >
      <header className="intro-topbar absolute inset-x-0 top-0 z-10 flex items-center justify-between text-fg-on-accent">
        <div className="flex items-center gap-2.5">
          <BrandMark size={32} className="ring-1 ring-fg-on-accent/30" />
          <span className="font-display text-xl font-bold tracking-tight">PeopleOrbit</span>
        </div>
        {!onLast && (
          <button
            type="button"
            className="rounded-full px-3 py-1.5 text-[0.9375rem] font-semibold text-fg-on-accent transition-colors hover:bg-fg-on-accent/12"
            onClick={() => leave('/signup')}
          >
            Skip
          </button>
        )}
      </header>

      <div
        role="region"
        aria-roledescription="carousel"
        aria-label="What PeopleOrbit does"
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="flex min-h-0 flex-1 flex-col outline-none"
      >
        <div ref={trackRef} className="intro-track flex min-h-0 flex-1">
          {INTRO_SLIDES.map(({ id, title, points }, i) => (
            <section
              key={id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${INTRO_SLIDES.length}`}
              data-active={i === index || undefined}
              data-tone={id}
              className="intro-slide flex w-full shrink-0 flex-col"
            >
              <div className="intro-hero relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
                <IntroOrbits />
                <IntroIllustration id={id} />
              </div>
              <div className="intro-copy px-6 pt-6">
                <h2 className="intro-ink font-display text-[length:clamp(1.375rem,6.6vw,1.75rem)] font-bold leading-[1.15] tracking-tight">
                  {title}
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-[0.9375rem] leading-snug text-fg-muted">
                      <span className="intro-check mt-px flex size-6 shrink-0 items-center justify-center rounded-full">
                        <Check className="size-3.5" strokeWidth={3} aria-hidden="true" />
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ))}
        </div>
      </div>

      <footer className="intro-footer flex flex-col items-center gap-3 px-6 pt-3">
        <div className="flex items-center gap-0.5">
          {INTRO_SLIDES.map(({ id }, i) => (
            <button
              key={id}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === index ? 'true' : undefined}
              onClick={() => goTo(i)}
              className="flex size-6 items-center justify-center rounded-full"
            >
              <span
                className={cn(
                  'block h-2 rounded-full transition-all duration-300',
                  i === index ? 'w-7 bg-accent' : 'w-2 bg-accent/25',
                )}
              />
            </button>
          ))}
        </div>

        <div className="flex w-full max-w-sm flex-col">
          <Button
            variant="primary"
            size="lg"
            className="h-[3.25rem] w-full rounded-2xl text-base font-semibold shadow-accent transition-colors sm:h-[3.25rem]"
            onClick={onLast ? () => leave('/signup') : () => goTo(index + 1)}
          >
            {onLast ? 'Get started' : 'Next'}
            {onLast && <ArrowRight className="size-4" aria-hidden="true" />}
          </Button>
          {onLast ? (
            <button
              type="button"
              className="flex h-11 items-center justify-center text-sm text-fg-muted"
              onClick={() => leave('/login')}
            >
              <span>
                Already have an account? <span className="font-semibold text-fg">Sign in</span>
              </span>
            </button>
          ) : (
            // Holds the Sign in row's height so the dots and Next don't jump on the last slide.
            <div className="h-11" aria-hidden="true" />
          )}
        </div>
      </footer>
    </div>
  );
}
